import { useMutation } from '@apollo/client'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  NotificationType,
  TextField,
} from '../../../components'
import { UPDATE_CREATOR, UpdateCreatorResponse } from '../../../gql'
import {
  Creator,
  CreatorOnboardingStep,
  MutationUpdateCreatorArgs,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import { useNotification } from '../../../utils/hooks'
import { Steps } from '../components/Steps'
import { Bullets } from '../components/Bullets'

export type EditPaypalEmailFormData = {
  paypalEmail: string
}

interface Props {
  creator: Creator
}

const SetPaypalEmailAddress = ({ creator }: Props) => {
  const { addNotification } = useNotification()

  const [updateCreator, { loading }] = useMutation<
    UpdateCreatorResponse,
    MutationUpdateCreatorArgs
  >(UPDATE_CREATOR)

  const {
    register,
    handleSubmit,
    control,
    setValue,
    // setError,
    formState: { errors },
    watch,
  } = useForm<EditPaypalEmailFormData>({
    defaultValues: {
      paypalEmail: creator?.paypalEmail ?? null,
    },
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsOnboardingSetPaypal)
  }, [])

  const goBack = async () => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setShippingAddress,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  const onSubmit = async (data?: EditPaypalEmailFormData) => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            paypalEmail: data?.paypalEmail,
            onboarding: CreatorOnboardingStep.setSocialMedia,
          },
        },
      })
    } catch (error) {
      console.log('@error SetProfile', error?.message)
      addNotification(error?.message, NotificationType.error)
    }
  }

  const PaypalEmailField = () => {
    return (
      <TextField
        // label='Paypal Email Address'
        name='paypalEmail'
        placeholder={creator.paypalEmail}
        error={errors.paypalEmail?.message}
        {...register('paypalEmail', {
          // required: `Please set an paypal email address.`,
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: `Please use a real paypal email address.`,
          },
          maxLength: {
            value: 100,
            message: `Please use a real paypal email address.`,
          },
        })}
      />
    )
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          creator={creator}
          stepCurrent={CreatorOnboardingStep.setPaypalEmailAddress}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8 md:bg-white'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      Paypal Email Address
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      Please enter the email address associated with your Paypal
                      account so that we can process your earnings.
                    </p>
                  </div>

                  <PaypalEmailField />
                </div>

                <p
                  className='text-sm my-4 text-center cursor-pointer'
                  onClick={() => onSubmit()}
                >
                  I'll add this later
                </p>

                <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                  <Button
                    title='Go back'
                    type='button'
                    colour={ButtonColor.white}
                    loading={loading}
                    disabled={loading}
                    onClick={goBack}
                  />
                  <Button
                    title='Continue'
                    type='submit'
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={CreatorOnboardingStep.setPaypalEmailAddress} />
    </div>
  )
}

export { SetPaypalEmailAddress }
